import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { any, equals, find } from 'ramda';
import { connect } from 'react-redux';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IRefReqSelectedContact } from '../../../../common/models/contact/IRefReqSelectedContact';
import ReviewItem from '../ReviewItem';
import { RequestBuilderPage, steps } from '../RequestBuilder/utils';
import { IBindingCallback1 } from '../../../../common/models/callback';
import Checkbox from '../../../../components/Checkbox';
import { setEmailTemplateRoutine, setReviewReferencesEnabledRoutine, setSendSMSToAllRoutine } from '../../routines';
import MessagePreview from '../MessagePreview';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import LinkButton from '../../../../components/LinkButton';
import ModalWindow from '../../../../components/ModalWindow';
import { IEmailTemplate } from '../../../../common/models/email/IEmailTemplate';
import Icon from '../../../../components/Icon';
import { INewContactInfo } from '../../../../common/models/contact/INewContactInfo';
import styles from './styles.module.scss';
import { EmailType } from '../../../../common/enums/EmailType';

interface IProps {
  candidates: IRefReqSelectedContact[];
  sendSMS: boolean;
  emailTemplate?: IEmailTemplate;
  references: INewContactInfo[] | null;
  defaultTemplates?: IEmailTemplate[];
  reviewReferencesEnabled: boolean;
  workspaceReviewRefEnabled: boolean;
  goToStep: IBindingCallback1<RequestBuilderPage>;
  setSendSMSToAll: IBindingCallback1<boolean>;
  setReviewReferencesEnabled: IBindingCallback1<boolean>;
  setEmailTemplate: IBindingCallback1<IEmailTemplate>;
}

const Review: FunctionComponent<IProps> = ({
  sendSMS,
  candidates,
  references,
  emailTemplate,
  defaultTemplates,
  reviewReferencesEnabled,
  workspaceReviewRefEnabled,
  goToStep,
  setSendSMSToAll,
  setEmailTemplate,
  setReviewReferencesEnabled
}) => {
  const [isPreviewShown, togglePreview] = useState(false);

  const onHide = () => togglePreview(false);

  const defaultMessage = find(({ type }) => (references
    ? type === EmailType.ReferenceRefRequest
    : type === EmailType.CandidateRefRequest
  ), defaultTemplates || []);

  useEffect(() => {
    const anyWithPhoneNumber = any(({ phoneNumber }) => Boolean(phoneNumber), references || candidates);
    setSendSMSToAll(anyWithPhoneNumber);
    setEmailTemplate(defaultMessage as IEmailTemplate);
  }, []);

  return (
    <div className={styles.review}>
      <ModalWindow
        isShown={isPreviewShown}
        onHide={onHide}
        contentClassName={`${styles.previewModal} modal-wrapper`}
      >
        <MessagePreview onHide={onHide} />
      </ModalWindow>
      <header className={styles.step5Header}>
        <FormattedMessage id="review.header" defaultMessage="Review and send" />
        {references && (
          <>
            &nbsp;
            <FormattedMessage id="review.headerRef" defaultMessage="survey to references" />
          </>
        )}
      </header>
      {workspaceReviewRefEnabled && (
        <div className={styles.container}>
          <div className={`${styles.grayText} mb-2`}>
            <FormattedMessage id="review.manual" defaultMessage="Manual intervention" />
          </div>
          <Checkbox
            checked={reviewReferencesEnabled}
            onChange={({ target }) => setReviewReferencesEnabled(target.checked)}
            label={{
              id: 'review.manualCheckbox',
              defaultMessage: 'I want to review after the candidate provides their references'
            }}
          />
          <div className={`${styles.grayText} mt-2 d-flex`}>
            <Icon className={styles.infoIcon} icon="info" />
            <span className={styles.tipText}>
              <FormattedMessage
                id="review.manualTip"
                defaultMessage={`Check this box if you don’t want to automatically send surveys to all 
                provided references. We will notify you when references are provided.`}
              />
            </span>
          </div>
        </div>
      )}
      <div className={styles.container}>
        <header className="d-flex align-items-center justify-content-between">
          <div>
            <span className={styles.grayText}>
              {references
                ? <FormattedMessage id="review.emailTitleReference" defaultMessage="Email to references" />
                : <FormattedMessage id="review.emailTitleCandidate" defaultMessage="Email to candidate" />}
            </span>
            <div className={styles.containerHeader}>
              {equals(defaultMessage, emailTemplate)
                ? <FormattedMessage id="review.defaultMessage" defaultMessage="My default message" />
                : <FormattedMessage id="review.customMessage" defaultMessage="Custom message" />}
            </div>
          </div>
          <LinkButton
            textIntlId="review.customize"
            defaultMessage="Customize message"
            icon={<PlusIcon />}
            onClick={() => togglePreview(true)}
          />
        </header>
        <main className={styles.containerBody}>
          <Checkbox
            checked={sendSMS}
            onChange={({ target }) => setSendSMSToAll(target.checked)}
            label={{ id: 'review.sendSMS', defaultMessage: 'Send SMS text message as well (recommended)' }}
          />
        </main>
      </div>
      <div className={styles.stepsReview}>
        {steps.map((step, index) => (step === RequestBuilderPage.Step5
          ? null
          : <ReviewItem order={index + 1} key={step} onEdit={goToStep} step={step} />
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = ({
  requestBuilder: {
    candidates, refCounters, sendSMS, emailTemplate, defaultTemplates, reviewReferencesEnabled, references
  },
  workspace: { reviewReferencesEnabled: workspaceReviewRefEnabled }
}: IAppState) => ({
  sendSMS,
  candidates,
  references,
  refCounters,
  emailTemplate,
  defaultTemplates,
  workspaceReviewRefEnabled: workspaceReviewRefEnabled as boolean,
  reviewReferencesEnabled
});

const mapDispachToProps = {
  setSendSMSToAll: setSendSMSToAllRoutine,
  setReviewReferencesEnabled: setReviewReferencesEnabledRoutine,
  setEmailTemplate: setEmailTemplateRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(Review);
